import axios, { type CancelTokenSource } from 'axios';
import {
    BillErrors,
    type BillListResponse,
    type BillResponse,
    BillsApiApiBillsBillCodeFullRefundGet,
    type BillsApiApiBillsBillCodeFullRefundGetRequest,
    BillsApiApiBillsBillCodeGet,
    type BillsApiApiBillsBillCodeGetRequest,
    UserBillsApiApiBillsGet,
    type UserBillsApiApiBillsGetRequest,
    type FullRefundResponse,
    type PenaltyInquiryResponse,
    type ReserveResponse,
    SuperPNRsApiApiSuperPNRsSuperPNRIdPenaltyPost,
} from '@clients/BillApi';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';

export default class TrackingStore {
    //#region state
    /**
     * List of loaded bills related to the current user
     *
     * @type {BillResponse[]}
     * @memberof TrackingStore
     */
    bills: BillResponse[] = [];
    prevBills: BillResponse[] = [];
    ongoingBills: BillResponse[] = [];
    ongoingBillRequestSource: CancelTokenSource | null = null;
    refunds: FullRefundResponse[] = [];
    /**
     * Count of pages of bills to keep track of
     *
     * @type {number | null}
     * @memberof TrackingStore
     */
    pageCount: number | null = null;

    /**
     * Total count of bills, regardless of pagination
     *
     * @type {number | null}
     * @memberof TrackingStore
     */
    totalCount: number | null = null;

    /**
     * Indicates the current page number
     *
     * @memberof TrackingStore
     */
    currentPage = 0;

    /**
     * Indicates the loading status of the current page
     *
     * @type {LoadingStatus}
     * @memberof TrackingStore
     */
    loadingStatus: LoadingStatus = LoadingStatus.None;

    /**
     * Indicates whether all the bill of user are loaded, or not
     *
     * @memberof TrackingStore
     */
    loadingFinished = false;

    /**
     * Keep current super pnr id for details of current reserve and current bill
     *
     * @type {(number | null)}
     * @memberof TrackingStore
     */
    currentSuperPNRId: number | null = null;

    /**
     * Loading status of penalties of trip cancellation
     *
     * @type {LoadingStatus}
     * @memberof TrackingStore
     */
    penaltyLoadingStatus: LoadingStatus = LoadingStatus.None;

    penaltyResult: PenaltyInquiryResponse | null = null;
    //#endregion

    /**
     * Loads the result of currently reserved trips bill
     *
     * @param {BillListResponse} result
     * @param currentState
     * @memberof TrackingStore
     */
    loadBillsResult(result: BillListResponse, currentState?: 0 | 1) {
        this.bills = this.bills.concat(result.Bills || []);
        if (currentState === 0) {
            this.ongoingBills = this.ongoingBills.concat(result.Bills || []);
        } else if (currentState === 1) {
            this.prevBills = this.prevBills.concat(result.Bills || []);
        }
        const totalCount = result.Meta?.TotalCount || 0;
        this.pageCount = Math.ceil(totalCount / (result.Meta?.PageSize || 0));
        this.totalCount = totalCount;
        this.loadingFinished = this.bills.length >= totalCount;
    }

    /**
     * Loads a single selected bill
     *
     * @param {BillResponse} bill
     * @memberof TrackingStore
     */
    loadSingleBill(bill: BillResponse) {
        if (!bill) {
            return;
        }
        this.bills = [bill];
    }

    /**
     * Clears all bills of reserved trips
     *
     * @memberof TrackingStore
     */
    clearBills() {
        this.bills = [];
    }

    /**
     * Resets all the reserved bills
     *
     * @memberof TrackingStore
     */
    reset() {
        this.currentPage = 0;
        this.bills = [];
        this.ongoingBills = [];
        this.prevBills = [];
    }

    /**
     * reset state of tracking module
     *
     * @memberof AuthStore
     */
    resetState() {
        this.bills = [];
        this.pageCount = null;
        this.totalCount = null;
        this.currentPage = 0;
        this.loadingStatus = LoadingStatus.None;
        this.loadingFinished = false;
        this.currentSuperPNRId = null;
        this.penaltyLoadingStatus = LoadingStatus.None;
        this.penaltyResult = null;
    }

    /**
     * Initiates the tracking information
     *
     * @memberof TrackingStore
     */
    init() {
        this.penaltyResult = null;
    }

    /**
     * GETTER - return the current reserved trip
     *
     * @memberof TrackingStore
     */
    get currentReserve(): ReserveResponse | null {
        return (
            this.currentBill?.Reserves?.find(
                r => r.SuperPNRId === this.currentSuperPNRId
            ) || null
        );
    }

    /**
     * GETTER - return the bill associated with the current reserved trip
     *
     * @memberof TrackingStore
     */
    get currentBill(): BillResponse | null {
        return (
            this.bills.find(bill =>
                bill.Reserves?.find(
                    reserve => reserve.SuperPNRId === this.currentSuperPNRId
                )
            ) || null
        );
    }

    get disableLoadMoreBills(): boolean {
        return (
            this.loadingStatus !== LoadingStatus.Success || this.loadingFinished
        );
    }

    /**
     * ACTION - Loads user's bills from server
     *
     * @async
     * @param {{
     *      reset: boolean;
     *      billCode?: string;
     *      passengerName?: string;
     *      from?: string;
     *      to?: string;
     *      SearchModes?: string[];
     *  }} payload
     * @param currentState
     * @memberof TrackingStore
     */
    async loadUserBills(
        payload: UserBillsApiApiBillsGetRequest = {},
        currentState?: 0 | 1
    ) {
        this.currentPage++;
        this.loadingStatus = LoadingStatus.Loading;
        try {
            if (this.ongoingBillRequestSource) {
                this.ongoingBillRequestSource.cancel('due to tab change');
            }
            const source = axios.CancelToken.source();
            this.ongoingBillRequestSource = source;
            const { data } = await UserBillsApiApiBillsGet(
                {
                    ...payload,
                    pageNumber: this.currentPage,
                },
                {
                    cancelToken: source.token,
                }
            );
            this.ongoingBillRequestSource = null;
            this.loadBillsResult(data, currentState);
            this.loadingStatus = LoadingStatus.Success;
        } catch (e) {
            reportException(e);
            this.loadingStatus = LoadingStatus.Error;
        }
    }

    /**
     * ACTION - Loads the selected bill from the server
     *
     * @async
     * @param {{ billCode: string; mobile: string }} payload
     * @memberof TrackingStore
     */
    async loadBill(payload: BillsApiApiBillsBillCodeGetRequest) {
        this.loadingStatus = LoadingStatus.Loading;
        this.clearBills();
        try {
            const { data, status } = await BillsApiApiBillsBillCodeGet(payload);
            if (status === 204) {
                throw BillErrors.NotFound;
            }
            this.loadSingleBill(data);
            this.loadingStatus = LoadingStatus.Success;
        } catch (error) {
            this.loadingStatus =
                error === BillErrors.NotFound
                    ? LoadingStatus.Success
                    : LoadingStatus.Error;
            throw error;
        }
    }

    async getRefunds(params: BillsApiApiBillsBillCodeFullRefundGetRequest) {
        const { data } = await BillsApiApiBillsBillCodeFullRefundGet(params);
        this.refunds = data;
    }

    /**
     * ACTION - Loads the penalty of current reservation
     *
     * @async
     * @memberof TrackingStore
     */
    async loadPenalty(currentReserve: ReserveResponse | undefined) {
        if (currentReserve === undefined) {
            return;
        }

        this.penaltyLoadingStatus = LoadingStatus.Loading;
        try {
            const { data } =
                await SuperPNRsApiApiSuperPNRsSuperPNRIdPenaltyPost({
                    superPNRId: currentReserve.SuperPNRId || 0,
                    superPNRPenaltyRequest: {
                        TicketIds: currentReserve.Tickets?.map(t => t.Id || 0),
                    },
                });
            this.penaltyResult = data;
            this.penaltyLoadingStatus = LoadingStatus.Success;
        } catch (ex) {
            this.penaltyLoadingStatus = LoadingStatus.Error;
            throw ex;
        }
    }

    async refreshBill(currentReserve: ReserveResponse | undefined) {
        if (!currentReserve) {
            return;
        }
        const currentBill =
            this.bills.find(bill =>
                bill.Reserves?.find(
                    reserve => reserve.SuperPNRId === currentReserve.SuperPNRId
                )
            ) || null;
        const billCode = currentBill?.BillCode || '';
        const mobile = currentBill?.Mobile || '';
        const { data } = await BillsApiApiBillsBillCodeGet({
            billCode,
            mobile,
        });
        const billsIndex = this.bills.findIndex(
            bill => bill.BillCode === billCode && bill.Mobile === mobile
        );
        if (billsIndex !== -1) {
            this.bills[billsIndex] = data;
        }
        const ongoingIndex = this.ongoingBills.findIndex(
            bill => bill.BillCode === billCode && bill.Mobile === mobile
        );
        if (ongoingIndex !== -1) {
            this.ongoingBills[ongoingIndex] = data;
        }
        const prevIndex = this.prevBills.findIndex(
            bill => bill.BillCode === billCode && bill.Mobile === mobile
        );
        if (prevIndex !== -1) {
            this.prevBills[prevIndex] = data;
        }
    }
}

export const { use: useTrackingStore } = defineClassStore(
    'tracking',
    () => new TrackingStore()
);
